/**
 * Streckenverwaltung - Enterprise-Pattern
 * 
 * Fachliches Modell:
 * - Hauptstrecke (ProcessingLine): z.B. "Mangel"
 * - Sub-Strecke (SubLine): z.B. "Mangel Linie 1", MUSS einer Hauptstrecke zugeordnet sein
 * - FL-Erfassung nutzt immer konkrete (Sub-)Strecken
 * 
 * UX-Leitlinie:
 * - Keine Create/Edit-Modals
 * - Klick auf Zeile → Detailseite
 * - Delete/Deactivate nur auf Detailseite
 * - Keine Trash-Icons in Listen
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Route, Plus, RefreshCw, Search, ChevronRight, ChevronDown } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface SubLine {
  id: string;
  name: string;
  lineId: string;
  isActive: boolean;
}

interface Line {
  id: string;
  name: string;
  isActive: boolean;
  subLines: SubLine[];
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function LinesPage() {
  const navigate = useNavigate();
  const [lines, setLines] = useState<Line[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());

  // Load lines
  const loadLines = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/lines', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLines(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Strecken:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadLines();
  }, []);

  // Filter + alphabetisch sortieren
  const filtered = useMemo(() => {
    const active = lines.filter((l: Line) => l.isActive !== false);
    const searched = !search ? active : active.filter((l: Line) => {
      const s = search.toLowerCase();
      return l.name.toLowerCase().includes(s) ||
        l.subLines.some((sl: SubLine) => sl.name.toLowerCase().includes(s));
    });
    return sortAlpha(searched);
  }, [lines, search]);

  // Toggle expand
  const toggleExpand = (lineId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedLines);
    if (newExpanded.has(lineId)) {
      newExpanded.delete(lineId);
    } else {
      newExpanded.add(lineId);
    }
    setExpandedLines(newExpanded);
  };

  // Count active sublines
  const countActiveSubLines = (line: Line): number => {
    return line.subLines?.filter((sl: SubLine) => sl.isActive !== false).length || 0;
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Route className="w-6 h-6" />
              Strecken
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {filtered.length} Hauptstrecken, {filtered.reduce((acc: number, l: Line) => acc + countActiveSubLines(l), 0)} Sub-Strecken
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadLines}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <Link 
              to="/admin/lines/new"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Strecke
            </Link>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Lines List */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {search ? 'Keine Treffer' : 'Keine Strecken vorhanden'}
              {!search && (
                <Link 
                  to="/admin/lines/new"
                  className="block mx-auto mt-4 text-blue-600 hover:underline"
                >
                  Erste Strecke anlegen
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((line: Line) => {
                const activeSubLines = line.subLines.filter((sl: SubLine) => sl.isActive !== false);
                const hasSubLines = activeSubLines.length > 0;
                
                return (
                  <div key={line.id}>
                    {/* Main Line Row */}
                    <div 
                      className="flex items-center px-4 py-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/admin/lines/${line.id}`)}
                    >
                      {hasSubLines ? (
                        <button
                          onClick={(e) => toggleExpand(line.id, e)}
                          className="p-1 mr-2 hover:bg-slate-200 rounded"
                        >
                          {expandedLines.has(line.id) ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                        </button>
                      ) : (
                        <div className="w-6 mr-2" />
                      )}
                      <div className="flex-1">
                        <span className="font-medium">{line.name}</span>
                        <span className="text-xs text-slate-400 ml-2">
                          Hauptstrecke ({activeSubLines.length} Sub-Strecken)
                        </span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                    
                    {/* SubLines */}
                    {expandedLines.has(line.id) && hasSubLines && (
                      <div className="bg-slate-50 border-t">
                        {activeSubLines.map((subLine: SubLine) => (
                          <div 
                            key={subLine.id}
                            className="flex items-center px-4 py-2 pl-12 hover:bg-slate-100 cursor-pointer border-b border-slate-100 last:border-b-0"
                            onClick={() => navigate(`/admin/sublines/${subLine.id}`)}
                          >
                            <span className="flex-1 text-sm">
                              {subLine.name}
                              <span className="text-xs text-slate-400 ml-2">Sub-Strecke</span>
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
